import api from './api';
import { 
  Submission, 
  SubmissionStats, 
  DeadlineStats, 
  SubmissionFilters, 
  BulkSendData,
  ExtendedBulkSendData,
  SubmissionsResponse,
  BulkOperationResult,
  ApiResponse
} from '../types/submission';

// ============================================================================
// SUBMISSION MANAGEMENT
// ============================================================================

export const getSubmissions = async (filters?: Partial<SubmissionFilters>): Promise<ApiResponse<SubmissionsResponse>> => {
  try {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    
    // 🔧 COSMOS DB FIX: Use main endpoint (now uses CosmosDB version with group headers)
    const response = await api.get(`/submissions?${params.toString()}`);
    return response;
    
  } catch (error: any) {
    console.error('Error fetching submissions:', error);
    throw error;
  }
};

export const getSubmission = async (id: string): Promise<ApiResponse<Submission>> => {
  return await api.get(`/submissions/${id}`);
};

export const createSubmission = async (data: Partial<Submission>): Promise<ApiResponse<Submission>> => {
  return await api.post('/submissions', data);
};

export const updateSubmission = async (id: string, data: Partial<Submission>): Promise<ApiResponse<Submission>> => {
  return await api.put(`/submissions/${id}`, data);
};

export const deleteSubmission = async (id: string): Promise<ApiResponse<{ message: string; id: string }>> => {
  return await api.delete(`/submissions/${id}`);
};

// ============================================================================
// BULK OPERATIONS
// ============================================================================

export const bulkDeleteSubmissions = async (submissionIds: string[]): Promise<ApiResponse<{ deletedCount: number; submissionIds: string[] }>> => {
  return await api.delete('/submissions/bulk', {
    data: { submissionIds }
  });
};

// **NUR NOCH BULK-SEND - KEINE EINZELNE SEND-FUNKTION**
export const bulkSendQuestionnaires = async (data: ExtendedBulkSendData): Promise<ApiResponse<BulkOperationResult>> => {
  console.log('📤 Sending bulk questionnaires:', {
    templateId: data.questionnaireTemplateId, // Legacy single template
    questionnaireTemplateIds: data.questionnaireTemplateIds, // NEW: Multiple templates
    templateCount: data.questionnaireTemplateIds?.length || (data.questionnaireTemplateId ? 1 : 0),
    supplierCount: data.supplierIds.length,
    deadline: data.deadline,
    conformity: data.sendConformityDeclaration,
    emergencyContact: data.sendEmergencyContact
  });
  
  return await api.post('/submissions/bulk-send', data);
};

export const bulkUpdateStatus = async (submissionIds: string[], status: string, reason?: string): Promise<ApiResponse<{ updatedCount: number }>> => {
  return await api.put('/submissions/bulk-status', {
    submissionIds,
    status,
    reason
  });
};

export const bulkAssign = async (submissionIds: string[], assignedTo: string, reason?: string): Promise<ApiResponse<{ updatedCount: number }>> => {
  return await api.put('/submissions/bulk-assign', {
    submissionIds,
    assignedTo,
    reason
  });
};

// ============================================================================
// STATUS MANAGEMENT
// ============================================================================

export const updateSubmissionStatus = async (id: string, status: string, reason?: string): Promise<ApiResponse<Submission>> => {
  return await api.put(`/submissions/${id}/status`, { status, reason });
};

export const approveSubmission = async (id: string, notes?: string): Promise<ApiResponse<Submission>> => {
  return await api.post(`/submissions/${id}/approve`, { notes });
};

export const rejectSubmission = async (id: string, reason: string): Promise<ApiResponse<Submission>> => {
  return await api.post(`/submissions/${id}/reject`, { reason });
};

export const reassignSubmission = async (id: string, assignedTo: string, reason?: string): Promise<ApiResponse<Submission>> => {
  return await api.put(`/submissions/${id}/assign`, { assignedTo, reason });
};

// ============================================================================
// NOTES & COMMENTS
// ============================================================================

export const addInternalNote = async (id: string, note: string, isPrivate = false): Promise<ApiResponse<Submission>> => {
  return await api.post(`/submissions/${id}/notes`, { note, isPrivate });
};

export const updateInternalNote = async (id: string, noteId: string, note: string, isPrivate = false): Promise<ApiResponse<Submission>> => {
  return await api.put(`/submissions/${id}/notes/${noteId}`, { note, isPrivate });
};

export const deleteInternalNote = async (id: string, noteId: string): Promise<ApiResponse<{ message: string }>> => {
  return await api.delete(`/submissions/${id}/notes/${noteId}`);
};

// ============================================================================
// REMINDERS & DEADLINES
// ============================================================================

export const scheduleReminder = async (id: string, scheduledFor: string, type = 'reminder'): Promise<ApiResponse<{ message: string }>> => {
  return await api.post(`/submissions/${id}/reminders`, { scheduledFor, type });
};

export const sendManualReminder = async (id: string, type = 'reminder', customMessage?: string): Promise<ApiResponse<{ message: string }>> => {
  return await api.post(`/submissions/${id}/reminder`, { type, customMessage });
};

export const updateDeadline = async (id: string, deadline: string, reason?: string): Promise<ApiResponse<Submission>> => {
  return await api.patch(`/submissions/${id}/deadline`, { deadline, reason });
};

// ============================================================================
// STATISTICS & REPORTING
// ============================================================================

export const getSubmissionStats = async (year?: number): Promise<ApiResponse<SubmissionStats>> => {
  const params = year ? `?year=${year}` : '';
  return await api.get(`/submissions/stats/overview${params}`);
};

export const getDeadlineStats = async (): Promise<ApiResponse<DeadlineStats>> => {
  return await api.get('/submissions/stats/deadlines');
};

export const getOverdueSubmissions = async (): Promise<ApiResponse<Submission[]>> => {
  return await api.get('/submissions/overdue');
};

export const getRemindersDue = async (): Promise<ApiResponse<Submission[]>> => {
  return await api.get('/submissions/reminders/due');
};

export const getSubmissionsBySupplier = async (supplierId: string, year?: number): Promise<ApiResponse<Submission[]>> => {
  const params = year ? `?year=${year}` : '';
  return await api.get(`/submissions/by-supplier/${supplierId}${params}`);
};

export const getSubmissionsByTemplate = async (templateId: string, year?: number): Promise<ApiResponse<Submission[]>> => {
  const params = year ? `?year=${year}` : '';
  return await api.get(`/submissions/by-template/${templateId}${params}`);
};

// ============================================================================
// EXPORT & REPORTING
// ============================================================================

export const exportSubmissions = async (format: 'xlsx' | 'csv' | 'pdf', filters?: Partial<SubmissionFilters>): Promise<any> => {
  const params = new URLSearchParams();
  
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
  }
  
  const response = await api.get(`/submissions/export/${format}?${params.toString()}`, {
    responseType: 'blob'
  });
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `submissions-${new Date().toISOString().split('T')[0]}.${format}`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  return response;
};

export const generateSubmissionReport = async (submissionId: string, format: 'pdf' | 'docx'): Promise<any> => {
  const response = await api.get(`/submissions/${submissionId}/report/${format}`, {
    responseType: 'blob'
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `submission-report-${submissionId}.${format}`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  return response;
};

// ============================================================================
// DASHBOARD WIDGETS
// ============================================================================

export const getDashboardData = async () => {
  const [stats, deadlines, overdue, reminders] = await Promise.allSettled([
    getSubmissionStats(),
    getDeadlineStats(),
    getOverdueSubmissions(),
    getRemindersDue()
  ]);
  
  return {
    stats: stats.status === 'fulfilled' ? stats.value.data : null,
    deadlines: deadlines.status === 'fulfilled' ? deadlines.value.data : null,
    overdue: overdue.status === 'fulfilled' ? overdue.value.data : null,
    reminders: reminders.status === 'fulfilled' ? reminders.value.data : null
  };
};

export const getMyAssignedSubmissions = async (limit = 10): Promise<ApiResponse<Submission[]>> => {
  return await api.get(`/submissions?assignedTo=me&limit=${limit}&status=sent,in_progress`);
};

export const getRecentActivity = async (limit = 20): Promise<ApiResponse<any[]>> => {
  return await api.get(`/submissions/activity/recent?limit=${limit}`);
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getFilterOptions = (): {
  statuses: Array<{ value: string; label: string }>;
  priorities: Array<{ value: string; label: string }>;
} => {
  return {
    statuses: [
      { value: 'all', label: 'Alle Status' },
      { value: 'draft', label: 'Entwurf' },
      { value: 'sent', label: 'Versendet' },
      { value: 'in_progress', label: 'In Bearbeitung' },
      { value: 'completed', label: 'Abgeschlossen' },
      { value: 'overdue', label: 'Überfällig' },
      { value: 'expired', label: 'Abgelaufen' }
    ],
    priorities: [
      { value: 'all', label: 'Alle Prioritäten' },
      { value: 'low', label: 'Niedrig' },
      { value: 'normal', label: 'Normal' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'Hoch' },
      { value: 'urgent', label: 'Dringend' }
    ]
  };
};

// **NEU: GET SUBMISSION ANSWERS**
export const getSubmissionAnswers = async (id: string): Promise<ApiResponse<{
  submission: {
    _id: string;
    status: string;
    progressPercentage: number;
    deadline: string;
    createdAt: string;
    sentAt?: string;
    completedAt?: string;
    answers: any[];
  };
  supplier: any;
  template: any;
  answeredQuestions: number;
  totalQuestions: number;
}>> => {
  return await api.get(`/submissions/${id}/answers`);
};

// **NEU: EXPORT SUBMISSION AS PDF BLOB (für Bulk-Download)**
export const exportSubmissionAsPDFBlob = async (id: string): Promise<{ blob: Blob; filename: string }> => {
  const response = await api.get(`/submissions/${id}/export/pdf`, {
    responseType: 'blob'
  });

  const blob = new Blob([response.data], { type: 'application/pdf' });

  const contentDisposition = response.headers['content-disposition'];
  let filename = `submission_${id}.pdf`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  return { blob, filename };
};

// **NEU: EXPORT SUBMISSION AS PDF**
export const exportSubmissionAsPDF = async (id: string): Promise<void> => {
  try {
    const response = await api.get(`/submissions/${id}/export/pdf`, {
      responseType: 'blob'
    });
    
    // **CREATE DOWNLOAD LINK**
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // **EXTRACT FILENAME FROM HEADERS**
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'fragebogen_antworten.pdf';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    link.download = filename;
    
    // **TRIGGER DOWNLOAD**
    document.body.appendChild(link);
    link.click();
    
    // **CLEANUP**
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ PDF download started:', filename);
    
  } catch (error: any) {
    console.error('❌ PDF export failed:', error);
    throw new Error(error.response?.data?.message || 'PDF-Export fehlgeschlagen');
  }
};

// **NEU: EXPORT SUBMISSION AS HTML**
export const exportSubmissionAsHTML = async (id: string): Promise<void> => {
  try {
    const response = await api.get(`/submissions/${id}/export/html`, {
      responseType: 'blob'
    });
    
    // **CREATE DOWNLOAD LINK**
    const blob = new Blob([response.data], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // **EXTRACT FILENAME FROM HEADERS**
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'fragebogen_antworten.html';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    link.download = filename;
    
    // **TRIGGER DOWNLOAD**
    document.body.appendChild(link);
    link.click();
    
    // **CLEANUP**
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ HTML download started:', filename);
    
  } catch (error: any) {
    console.error('❌ HTML export failed:', error);
    throw new Error(error.response?.data?.message || 'HTML-Export fehlgeschlagen');
  }
};

// **ATTACHMENT DOWNLOAD**
export const downloadSubmissionAttachment = async (submissionId: string, fileName: string): Promise<void> => {
  try {
    console.log(`📥 Downloading attachment: ${fileName} from submission: ${submissionId}`);
    
    const response = await api.get(`/submissions/${submissionId}/attachments/${encodeURIComponent(fileName)}`, {
      responseType: 'blob'
    });
    
    // **CREATE BLOB URL**
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    
    // **CREATE DOWNLOAD LINK**
    const link = document.createElement('a');
    link.href = url;
    
    // **EXTRACT FILENAME FROM RESPONSE HEADERS IF AVAILABLE**
    let downloadFileName = fileName;
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch) {
        downloadFileName = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    link.download = downloadFileName;
    
    // **TRIGGER DOWNLOAD**
    document.body.appendChild(link);
    link.click();
    
    // **CLEANUP**
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Attachment download completed:', downloadFileName);
    
  } catch (error: any) {
    console.error('❌ Attachment download failed:', error);
    throw new Error(error.response?.data?.message || 'Datei-Download fehlgeschlagen');
  }
};

// **GET ATTACHMENT PREVIEW (FOR IMAGES)**
export const getAttachmentPreview = async (submissionId: string, fileName: string): Promise<string | null> => {
  try {
    console.log(`🖼️ Getting image preview: ${fileName} from submission: ${submissionId}`);
    
    const response = await api.get(`/submissions/${submissionId}/attachments/${encodeURIComponent(fileName)}`, {
      responseType: 'blob'
    });
    
    // **CREATE BLOB URL FOR PREVIEW**
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    
    console.log('✅ Image preview URL created');
    return url;
    
  } catch (error: any) {
    console.error('❌ Image preview failed:', error);
    return null;
  }
};

// ============================================================================
// NEW: DUPLICATE CHECKING & CONFORMITY DECLARATION FEATURES
// ============================================================================

export interface DuplicateCheckResult {
  success: boolean;
  year: number;
  duplicates: Array<{
    supplierId: string;
    supplierName: string;
    supplierEmail: string;
    status: string;
    deadline: string;
    createdAt: string;
  }>;
  hasDuplicates: boolean;
  message: string;
}

export interface ConformityTemplate {
  _id: string;
  title: {
    de?: string;
    en?: string;
  };
  description?: string;
  type: string;
  version?: string;
}

export interface ConformityTemplatesResult {
  success: boolean;
  templates: ConformityTemplate[];
  count: number;
}

// **CHECK FOR DUPLICATE SUBMISSIONS - ENHANCED FOR CONFORMITY DECLARATIONS**
export const checkDuplicateSubmissions = async (
  supplierIds: string[], 
  questionnaireTemplateId: string, 
  year?: number,
  // NEW: Conformity declaration support
  sendConformityDeclaration?: boolean,
  // NEW: Emergency contact support
  sendEmergencyContact?: boolean,
  conformityTemplateId?: string,
  emergencyContactTemplateId?: string
): Promise<ApiResponse<DuplicateCheckResult>> => {
  try {
    const response = await api.post('/submissions/check-duplicates', {
      supplierIds,
      questionnaireTemplateId,
      year: year || new Date().getFullYear(),
      sendConformityDeclaration: sendConformityDeclaration || false,
      sendEmergencyContact: sendEmergencyContact || false,
      conformityTemplateId,
      emergencyContactTemplateId
    });
    
    return response;
    
  } catch (error: any) {
    console.error('Error checking duplicates:', error);
    throw error;
  }
};

// **GET CONFORMITY DECLARATION TEMPLATES**
export const getConformityTemplates = async (): Promise<ApiResponse<ConformityTemplatesResult>> => {
  try {
    const response = await api.get('/submissions/conformity-templates');
    return response;
    
  } catch (error: any) {
    console.error('Error fetching conformity templates:', error);
    throw error;
  }
};

// **EXTENDED BULK SEND DATA WITH CONFORMITY SUPPORT**

// ============================================================================
// NEW: ARTICLE-BASED EXPORT FUNCTIONALITY
// ============================================================================

export interface ArticleExportOptions {
  supplierIds?: string[];
  articleIds?: string[];
  status?: string[];
  fromDate?: string;
  toDate?: string;
  format?: 'xlsx' | 'csv' | 'pdf' | 'zip';
  includeArticleDetails?: boolean;
  includeSubmissionHistory?: boolean;
  includeAllAnswers?: boolean;
  groupBySupplier?: boolean;
  groupByArticle?: boolean;
  filters?: Partial<SubmissionFilters>;
}

export interface ArticleFromSubmission {
  _id: string;
  articleId: string;
  articleNumber: string;
  articleName: string;
  name: string; // Alias for articleName
  gtin?: string;
  brand?: string;
  supplierId: string;
  supplierName: string;
  submissionId: string;
  submissionStatus: string;
  submissionDeadline?: string;
  lastModified?: string;
  completionPercentage?: number;
  submissionCount: number;
  supplierCount: number;
}

/**
 * Get unique articles from submissions based on filters
 */
export const getArticlesFromSubmissions = async (filters?: Partial<SubmissionFilters>): Promise<ApiResponse<{
  articles: ArticleFromSubmission[];
  totalArticles: number;
  totalSubmissions: number;
}>> => {
  try {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    
    return await api.get(`/submissions/articles/list?${params.toString()}`);
    
  } catch (error: any) {
    console.error('Error fetching articles from submissions:', error);
    throw error;
  }
};

/**
 * Export submissions grouped/filtered by articles
 */
export const exportSubmissionsByArticle = async (options: ArticleExportOptions): Promise<void> => {
  try {
    const format = options.format || 'xlsx';
    
    const response = await api.post(`/submissions/export/by-article/${format}`, options, {
      responseType: 'blob'
    });
    
    // Create download link
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from headers
    const contentDisposition = response.headers['content-disposition'];
    let filename = `article-submissions-${new Date().toISOString().split('T')[0]}.${format}`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Article-based export download started:', filename);
    
  } catch (error: any) {
    console.error('❌ Article-based export failed:', error);
    throw new Error(error.response?.data?.message || 'Artikel-Export fehlgeschlagen');
  }
};
